import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./App.css";
import { AppProvider, useApp } from "./presentation/contexts/AppContext";
import { OrderCard } from "./presentation/components/OrderCard";
import AnalyticsDashboard from "./presentation/components/AnalyticsDashboard";
import { useOrders } from "./presentation/hooks/useOrders";
import { ApiService } from "./infrastructure/services/ApiService";
import { CreateProductRequest, Customer, Product } from "./types";
import {
  canAddProduct,
  canEditProduct,
  canDeleteProduct,
  canAddWarehouse,
  canAddCustomer,
  canViewAnalytics,
} from "./utils/permissions";

// Tema context'i
interface ThemeContextType {
  theme: "light" | "dark";
  toggleTheme: () => void;
}

export const ThemeContext = React.createContext<ThemeContextType>({
  theme: "light",
  toggleTheme: () => {},
});

const AppContent: React.FC = () => {
  const { state, dispatch } = useApp();
  const { handleOrderAction, deleteOrder, filteredOrders } = useOrders();

  const apiService = useMemo(() => new ApiService(), []);

  // Sidebar state - Always open
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Update body class for mobile sidebar
  useEffect(() => {
    const isMobile = window.innerWidth <= 1024;
    if (isMobile) {
      if (sidebarOpen) {
        document.body.classList.add("sidebar-open");
      } else {
        document.body.classList.remove("sidebar-open");
      }
    }

    // Cleanup function
    return () => {
      if (isMobile) {
        document.body.classList.remove("sidebar-open");
      }
    };
  }, [sidebarOpen]);

  // Login state
  const [loginCredentials, setLoginCredentials] = useState({
    username: "",
    password: "",
  });
  const [loginLoading, setLoginLoading] = useState(false);

  // Order creation state
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [newOrder, setNewOrder] = useState<any>({
    customerId: "",
    customerName: "",
    items: [],
    notes: "",
  });

  // Customer creation state
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    companyName: "",
    taxNumber: "",
    taxOffice: "",
    website: "",
  });

  // Product creation state
  const [showProductModal, setShowProductModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    productCode: "",
    description: "",
    unitPrice: 0,
    taxRate: 18,
    stockQuantity: 0,
    unit: "adet",
    selectedWarehouses: [] as number[], // Seçilen depolar
  });

  // Warehouse state
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [newWarehouse, setNewWarehouse] = useState({
    ad: "",
    il: "",
    ilce: "",
    detay_adres: "",
    soguk_zincir_destegi: false,
    soguk_zincir_sicaklik: 0,
    soguk_zincir_min: 0,
    soguk_zincir_max: 0,
    telefon: "",
    email: "",
    kapasite: 0,
    depo_tipi: "genel",
  });

  // Customer and Product lists state
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Orders tab state
  const [ordersTab, setOrdersTab] = useState<
    "active" | "cancelled" | "pending_approval"
  >("active");

  const { theme, toggleTheme } = React.useContext(ThemeContext);

  // Filter orders based on tab using filteredOrders from useOrders hook
  const getFilteredOrdersByTab = useCallback(() => {
    if (!Array.isArray(filteredOrders)) return [];

    if (ordersTab === "active") {
      return filteredOrders.filter(
        (order) =>
          order.status !== "cancelled" && order.status !== "pending_approval"
      );
    } else if (ordersTab === "pending_approval") {
      return filteredOrders.filter(
        (order) => order.status === "pending_approval"
      );
    } else {
      return filteredOrders.filter((order) => order.status === "cancelled");
    }
  }, [filteredOrders, ordersTab]);

  // Reactive tab counts - automatically update when orders change
  const activeOrdersCount = useMemo(() => {
    return filteredOrders.filter(
      (order) =>
        order.status !== "cancelled" && order.status !== "pending_approval"
    ).length;
  }, [filteredOrders]);

  const pendingOrdersCount = useMemo(() => {
    return filteredOrders.filter((order) => order.status === "pending_approval")
      .length;
  }, [filteredOrders]);

  const cancelledOrdersCount = useMemo(() => {
    return filteredOrders.filter((order) => order.status === "cancelled")
      .length;
  }, [filteredOrders]);

  const loadDashboardData = useCallback(async () => {
    // Load orders for dashboard
    const ordersResponse = await apiService.getOrders();
    if (ordersResponse.success && ordersResponse.data) {
      dispatch({ type: "SET_ORDERS", payload: ordersResponse.data });
    }

    // Load analytics for dashboard
    const analyticsResponse = await apiService.getAnalytics();
    if (analyticsResponse.success && analyticsResponse.data) {
      dispatch({ type: "SET_ANALYTICS", payload: analyticsResponse.data });
    }
  }, [dispatch, apiService]);

  // Load customers and products for order creation
  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const response = await apiService.getCustomers();
      if (response.success && response.data && Array.isArray(response.data)) {
        setCustomers(response.data);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error("❌ Müşteri yükleme hatası:", error);
    } finally {
      setLoadingCustomers(false);
    }
  }, [apiService]);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const response = await apiService.getProducts();
      if (response.success && response.data && Array.isArray(response.data)) {
        setProducts(response.data);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("❌ Ürün yükleme hatası:", error);
    } finally {
      setLoadingProducts(false);
    }
  }, [apiService]);

  const loadOrdersData = useCallback(async () => {
    // Load orders for orders tab
    const ordersResponse = await apiService.getOrders();
    if (ordersResponse.success && ordersResponse.data) {
      dispatch({ type: "SET_ORDERS", payload: ordersResponse.data });
    }
  }, [dispatch, apiService]);

  const loadWarehouses = useCallback(async () => {
    setLoadingWarehouses(true);
    try {
      const response = await apiService.getWarehouses();
      if (response.success && response.data && Array.isArray(response.data)) {
        setWarehouses(response.data);
      } else {
        setWarehouses([]);
      }
    } catch (error) {
      console.error("Error loading warehouses:", error);
    } finally {
      setLoadingWarehouses(false);
    }
  }, [apiService]);

  const loadAnalyticsData = useCallback(async () => {
    // Load analytics for analytics tab
    const analyticsResponse = await apiService.getAnalytics();
    if (analyticsResponse.success && analyticsResponse.data) {
      dispatch({ type: "SET_ANALYTICS", payload: analyticsResponse.data });
    }
  }, [apiService, dispatch]);

  const loadEssentialData = useCallback(async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      // Check if user is authenticated
      const token = localStorage.getItem("authToken");
      if (!token) {
        dispatch({ type: "SET_LOADING", payload: false });
        return;
      }

      // Load current user first
      const userResponse = await apiService.getCurrentUser();
      if (userResponse.success && userResponse.data) {
        dispatch({ type: "SET_USER", payload: userResponse.data });
      } else {
        // Token is invalid, remove it
        apiService.removeAuthToken();
        dispatch({
          type: "SET_ERROR",
          payload: "Oturum süresi dolmuş. Lütfen tekrar giriş yapın.",
        });
        dispatch({ type: "SET_LOADING", payload: false });
        return;
      }

      // Load notifications (always needed for badge)
      const notificationsResponse = await apiService.getNotifications();
      if (notificationsResponse.success && notificationsResponse.data) {
        dispatch({
          type: "SET_NOTIFICATIONS",
          payload: notificationsResponse.data,
        });
      }

      // Load settings (always needed)
      const settingsResponse = await apiService.getSettings();
      if (settingsResponse.success && settingsResponse.data) {
        dispatch({ type: "SET_SETTINGS", payload: settingsResponse.data });
      }
    } catch (err) {
      dispatch({
        type: "SET_ERROR",
        payload: "Veriler yüklenirken hata oluştu",
      });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [dispatch, apiService]);

  const handleLogin = useCallback(async () => {
    if (!loginCredentials.username || !loginCredentials.password) {
      alert("Lütfen kullanıcı adı ve şifre giriniz.");
      return;
    }

    setLoginLoading(true);
    try {
      const response = await apiService.login({
        username: loginCredentials.username,
        password: loginCredentials.password,
      });

      if (response.success) {
        await loadEssentialData();
        setLoginCredentials({ username: "", password: "" });
      } else {
        alert("Giriş başarısız: " + (response.error || "Bilinmeyen hata"));
      }
    } catch (error) {
      alert("Giriş sırasında hata oluştu: " + error);
    } finally {
      setLoginLoading(false);
    }
  }, [loginCredentials, apiService, loadEssentialData]);

  const handleCreateOrder = useCallback(async () => {
    if (!newOrder.customerId) {
      alert("Lütfen bir müşteri seçiniz.");
      return;
    }

    if (
      newOrder.items.length === 0 ||
      newOrder.items.some((item: any) => !item.productId)
    ) {
      alert("Lütfen en az bir ürün seçiniz.");
      return;
    }

    try {
      // Seçilen müşteriyi bul
      const selectedCustomer = customers.find(
        (c) => c.id == newOrder.customerId
      );
      if (!selectedCustomer) {
        alert("Seçilen müşteri bulunamadı.");
        return;
      }

      // Backend'in beklediği formatta sipariş verisi oluştur
      const orderData = {
        customer_id: newOrder.customerId, // customer_id ekle
        customer_name:
          selectedCustomer.name ||
          selectedCustomer.full_name ||
          selectedCustomer.company_name ||
          "",
        customer_phone: selectedCustomer.phone || "",
        customer_address:
          typeof selectedCustomer.address === "string"
            ? selectedCustomer.address
            : selectedCustomer.address_city
              ? `${selectedCustomer.address_city}, ${selectedCustomer.address_district || ""}`
              : selectedCustomer.address?.street
                ? `${selectedCustomer.address.street}, ${selectedCustomer.address.city || ""}`
                : "",
        customer_email: selectedCustomer.email || "",
        items: newOrder.items.map((item: any) => {
          const selectedProduct = products.find((p) => p.id == item.productId);
          return {
            product_name: selectedProduct?.name || item.productName,
            quantity: item.quantity,
            unit_price: item.unitPrice,
          };
        }),
        notes: newOrder.notes || "",
      };

      const response = await apiService.createOrder(orderData);

      console.log("🔍 Raw API response:", response);
      console.log("🔍 Response success:", response.success);
      console.log("🔍 Response data:", response.data);

      if (response.success && response.data) {
        // Order created successfully

        // Backend'den gelen tam veriyi kullan, sadece gerekli alanları ekle
        const newOrder = {
          ...response.data,
          // Backend'den gelen veri eksikse fallback değerler kullan
          id: response.data.id || `temp-${Date.now()}`,
          status:
            response.data.status ||
            (state.user?.role === "manager" || state.user?.role === "admin"
              ? "approved"
              : "pending_approval"),
          // Eksik alanlar için fallback
          customer_name: response.data.customer_name || orderData.customer_name,
          customer_phone:
            response.data.customer_phone || orderData.customer_phone,
          customer_address:
            response.data.customer_address || orderData.customer_address,
          customer_email:
            response.data.customer_email || orderData.customer_email,
          total_amount: response.data.total_amount || 0,
          grand_total: response.data.grand_total || 0,
          items:
            response.data.items ||
            orderData.items.map((item: any) => ({
              id: `temp-${Date.now()}-${Math.random()}`,
              product_name: item.product_name,
              quantity: item.quantity,
              unit_price: item.unit_price,
              total_price: item.quantity * item.unit_price,
            })),
          // Timestamp ekle
          created_at: response.data.created_at || new Date().toISOString(),
          updated_at: response.data.updated_at || new Date().toISOString(),
        };

        console.log("🆕 Creating new order in state:", newOrder);
        console.log("🔍 Backend response data:", response.data);
        console.log("🔍 Calculated total_amount:", response.data.total_amount);
        console.log("🔍 Calculated grand_total:", response.data.grand_total);

        // Force immediate state update
        const updatedOrders = [...state.orders, newOrder];
        dispatch({
          type: "SET_ORDERS",
          payload: updatedOrders as any,
        });

        // Modal'ı kapat ve formu temizle
        setShowOrderModal(false);
        setNewOrder({
          customerId: "",
          customerName: "",
          items: [],
          notes: "",
        });

        // Otomatik olarak uygun sekme seçimi
        if (newOrder.status === "approved") {
          setOrdersTab("active");
          alert(
            "Sipariş başarıyla oluşturuldu ve otomatik onaylandı! Aktif Siparişler sekmesine yönlendiriliyorsunuz."
          );
        } else {
          setOrdersTab("pending_approval");
          alert(
            "Sipariş başarıyla oluşturuldu! Onay Bekliyor sekmesine yönlendiriliyorsunuz."
          );
        }

        // Single state update is sufficient - no need for multiple force updates
      } else {
        alert(
          "Sipariş oluşturulamadı: " + (response.error || "Bilinmeyen hata")
        );
      }
    } catch (error) {
      alert("Sipariş oluşturulurken hata oluştu: " + error);
    }
  }, [
    newOrder,
    apiService,
    state.orders,
    dispatch,
    customers,
    products,
    loadOrdersData,
  ]);

  const handleCreateCustomer = useCallback(async () => {
    if (!newCustomer.name.trim()) {
      alert("Lütfen müşteri adını giriniz.");
      return;
    }

    if (!newCustomer.email.trim()) {
      alert("Lütfen müşteri email adresini giriniz.");
      return;
    }

    try {
      const customerData = {
        name: newCustomer.name,
        email: newCustomer.email,
        phone: newCustomer.phone || "",
        address: newCustomer.address || "",
        companyName: newCustomer.companyName || "",
        taxNumber: newCustomer.taxNumber || "",
        taxOffice: newCustomer.taxOffice || "",
        website: newCustomer.website || "",
      };

      const response = await apiService.createCustomer(customerData);

      if (response.success && response.data) {
        // Yeni müşteriyi state'e ekle (eğer customers state'i varsa)
        // dispatch({ type: "SET_CUSTOMERS", payload: [...state.customers, response.data] });

        // Modal'ı kapat ve formu temizle
        setShowCustomerModal(false);
        setNewCustomer({
          name: "",
          email: "",
          phone: "",
          address: "",
          companyName: "",
          taxNumber: "",
          taxOffice: "",
          website: "",
        });

        alert("Müşteri başarıyla oluşturuldu!");
      } else {
        alert(
          "Müşteri oluşturulamadı: " + (response.error || "Bilinmeyen hata")
        );
      }
    } catch (error) {
      console.error("Müşteri oluşturma hatası:", error);
      alert("Müşteri oluşturulurken hata oluştu: " + error);
    }
  }, [newCustomer, apiService, customers]);

  const handleCreateProduct = useCallback(async () => {
    // Yetki kontrolü
    if (!state.user || !canAddProduct(state.user.role)) {
      alert("Ürün ekleme yetkiniz bulunmamaktadır.");
      return;
    }

    if (!newProduct.name.trim() || !newProduct.productCode.trim()) {
      alert("Lütfen ürün adı ve ürün kodunu giriniz.");
      return;
    }

    if (newProduct.unitPrice <= 0) {
      alert("Lütfen geçerli bir birim fiyatı giriniz.");
      return;
    }

    if (newProduct.selectedWarehouses.length === 0) {
      alert("Lütfen en az bir depo seçiniz.");
      return;
    }

    try {
      const productData = {
        isim: newProduct.name,
        barkod: newProduct.productCode,
        etkin_madde: newProduct.description,
        fiyat: newProduct.unitPrice,
        kdv_orani: newProduct.taxRate,
        stok: newProduct.stockQuantity,
        birim: newProduct.unit,
        kategori: "Genel",
        selectedWarehouses: newProduct.selectedWarehouses, // Seçilen depolar
      };

      const response = await apiService.createProduct(productData as any);

      if (response.success && response.data) {
        // Yeni ürünü state'e ekle
        setProducts([...products, response.data]);

        // Modal'ı kapat ve formu temizle
        setShowProductModal(false);
        setNewProduct({
          name: "",
          productCode: "",
          description: "",
          unitPrice: 0,
          taxRate: 18,
          stockQuantity: 0,
          unit: "adet",
          selectedWarehouses: [],
        });

        alert("Ürün başarıyla oluşturuldu ve seçilen depolara eklendi!");
      } else {
        alert("Ürün oluşturulamadı: " + (response.error || "Bilinmeyen hata"));
      }
    } catch (error) {
      console.error("Ürün oluşturma hatası:", error);
      alert("Ürün oluşturulurken hata oluştu: " + error);
    }
  }, [newProduct, apiService, products]);

  const handleCreateWarehouse = useCallback(async () => {
    // Yetki kontrolü
    if (!state.user || !canAddWarehouse(state.user.role)) {
      alert("Depo ekleme yetkiniz bulunmamaktadır.");
      return;
    }

    if (
      !newWarehouse.ad.trim() ||
      !newWarehouse.il.trim() ||
      !newWarehouse.ilce.trim()
    ) {
      alert("Lütfen depo adı, il ve ilçe bilgilerini giriniz.");
      return;
    }

    if (newWarehouse.kapasite <= 0) {
      alert("Lütfen geçerli bir kapasite giriniz.");
      return;
    }

    try {
      const warehouseData = {
        ad: newWarehouse.ad,
        il: newWarehouse.il,
        ilce: newWarehouse.ilce,
        detay_adres: newWarehouse.detay_adres,
        soguk_zincir_destegi: newWarehouse.soguk_zincir_destegi,
        soguk_zincir_sicaklik: newWarehouse.soguk_zincir_sicaklik,
        soguk_zincir_min: newWarehouse.soguk_zincir_min,
        soguk_zincir_max: newWarehouse.soguk_zincir_max,
        telefon: newWarehouse.telefon,
        email: newWarehouse.email,
        kapasite: newWarehouse.kapasite,
        depo_tipi: newWarehouse.depo_tipi,
      };

      const response = await apiService.createWarehouse(warehouseData as any);

      if (response.success && response.data) {
        // Yeni depoyu state'e ekle
        setWarehouses([...warehouses, response.data]);

        // Modal'ı kapat ve formu temizle
        setShowWarehouseModal(false);
        setNewWarehouse({
          ad: "",
          il: "",
          ilce: "",
          detay_adres: "",
          soguk_zincir_destegi: false,
          soguk_zincir_sicaklik: 0,
          soguk_zincir_min: 0,
          soguk_zincir_max: 0,
          telefon: "",
          email: "",
          kapasite: 0,
          depo_tipi: "genel",
        });

        alert("Depo başarıyla oluşturuldu!");
      } else {
        alert("Depo oluşturulamadı: " + (response.error || "Bilinmeyen hata"));
      }
    } catch (error) {
      console.error("Depo oluşturma hatası:", error);
      alert("Depo oluşturulurken hata oluştu: " + error);
    }
  }, [newWarehouse, apiService, warehouses, state.user]);

  // Load essential data on component mount
  useEffect(() => {
    loadEssentialData();
  }, [loadEssentialData]);

  // Load data based on active tab
  useEffect(() => {
    if (state.user) {
      loadDashboardData();
      loadOrdersData();
    }
  }, [state.user, loadDashboardData, loadOrdersData]);

  // Load customers and products when order modal is opened
  useEffect(() => {
    if (showOrderModal) {
      loadCustomers();
      loadProducts();
    }
  }, [showOrderModal, loadCustomers, loadProducts]);

  // Load warehouses when product modal is opened
  useEffect(() => {
    if (showProductModal) {
      loadWarehouses();
    }
  }, [showProductModal, loadWarehouses]);

  // Load dashboard data when user is authenticated
  useEffect(() => {
    if (state.user && state.activeTab === "dashboard") {
      loadDashboardData();
    }
  }, [state.user, state.activeTab, loadDashboardData]);

  // Load orders data when orders tab is active
  useEffect(() => {
    if (state.user && state.activeTab === "orders") {
      loadOrdersData();
    }
  }, [state.user, state.activeTab, loadOrdersData]);

  // Load customers data when customers tab is active
  useEffect(() => {
    if (state.user && state.activeTab === "customers") {
      loadCustomers();
    }
  }, [state.user, state.activeTab, loadCustomers]);

  // Debug: Monitor state.orders changes (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("🔍 State.orders changed:", {
        ordersCount: state.orders.length,
        cancelledCount: state.orders.filter((o) => o.status === "cancelled")
          .length,
        pendingCount: state.orders.filter(
          (o) => o.status === "pending_approval"
        ).length,
        deletedCount: state.orders.filter((o) => o.status === "deleted").length,
        activeCount: state.orders.filter(
          (o) =>
            o.status !== "cancelled" &&
            o.status !== "pending_approval" &&
            o.status !== "deleted"
        ).length,
      });
    }
  }, [state.orders]);

  // Auto-switch to cancelled tab when orders are cancelled
  useEffect(() => {
    const cancelledOrders = state.orders.filter(
      (order) => order.status === "cancelled"
    );
    const hasCancelledOrders = cancelledOrders.length > 0;

    // If we're currently viewing a cancelled order and we're not on the cancelled tab
    if (hasCancelledOrders && ordersTab !== "cancelled") {
      // Check if the user just cancelled an order (within last 5 seconds)
      const recentlyCancelled = cancelledOrders.some((order) => {
        const updatedAt = new Date(
          order.updated_at || order.created_at || Date.now()
        );
        const now = new Date();
        const diffInSeconds = (now.getTime() - updatedAt.getTime()) / 1000;
        return diffInSeconds < 5; // Within last 5 seconds
      });

      if (recentlyCancelled) {
        console.log(
          "🔄 Auto-switching to cancelled tab due to recent cancellation"
        );
        setOrdersTab("cancelled");
      }
    }
  }, [state.orders, ordersTab]);

  const handleOrderActionWrapper = useCallback(
    async (orderId: string, action: string) => {
      if (!state.user) {
        return;
      }

      try {
        const result = await handleOrderAction(orderId, action, state.user);

        if (result?.success) {
          // Order action completed successfully
          if (action === "delete") {
            alert("🗑️ Sipariş başarıyla silindi!");
            // No need to reload orders - useOrders hook already updates the state
            // The UI will automatically update because getFilteredOrdersByTab filters out deleted orders
          } else if (action === "cancel") {
            alert("✅ Sipariş başarıyla iptal edildi!");
          } else if (action === "approve") {
            alert("✅ Sipariş başarıyla onaylandı!");
          } else if (action === "prepare") {
            alert("✅ Sipariş hazırlanmaya başlandı!");
          } else if (
            action === "ready" &&
            result.newStatus === "ready_for_shipping"
          ) {
            alert("✅ Sipariş hazır duruma getirildi!");
          } else if (action === "ship" && result.newStatus === "in_transit") {
            alert("✅ Sipariş yola çıkarıldı!");
          } else if (action === "deliver" && result.newStatus === "delivered") {
            alert("✅ Sipariş teslim edildi!");
          }
        } else {
          if (result?.error) {
            alert(`❌ Sipariş işlemi başarısız: ${result.error}`);
          }
        }
      } catch (error) {
        alert("❌ Sipariş işlemi sırasında hata oluştu!");
      }
    },
    [handleOrderAction, state.user]
  );

  // Check if user is authenticated
  const isAuthenticated = !!localStorage.getItem("authToken") && state.user;

  if (!isAuthenticated) {
    return (
      <div className="App">
        <header className="header">
          <div className="header-content">
            <h1>OrderApp</h1>
          </div>
        </header>
        <main className="main">
          <div className="login-section">
            <div className="login-card">
              <h2>Giriş Yapın</h2>
              <p>Devam etmek için lütfen giriş yapın.</p>
              <div className="login-form">
                <div className="form-group">
                  <label htmlFor="username">Kullanıcı Adı:</label>
                  <input
                    type="text"
                    id="username"
                    value={loginCredentials.username}
                    onChange={(e) =>
                      setLoginCredentials({
                        ...loginCredentials,
                        username: e.target.value,
                      })
                    }
                    placeholder="Kullanıcı adınızı girin"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Şifre:</label>
                  <input
                    type="password"
                    id="password"
                    value={loginCredentials.password}
                    onChange={(e) =>
                      setLoginCredentials({
                        ...loginCredentials,
                        password: e.target.value,
                      })
                    }
                    placeholder="Şifrenizi girin"
                  />
                </div>
                <button
                  className="login-button"
                  onClick={handleLogin}
                  disabled={loginLoading}
                >
                  {loginLoading ? "Giriş Yapılıyor..." : "Giriş Yap"}
                </button>
              </div>
              <div className="demo-credentials">
                <h3>Test Kullanıcı Bilgileri:</h3>
                <p>
                  <strong>Admin:</strong> admin / admin
                </p>
                <p>
                  <strong>Manager:</strong> manager / manager
                </p>
                <p>
                  <strong>Sales:</strong> sales / sales
                </p>
                <p>
                  <strong>Warehouse:</strong> warehouse / warehouse
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className="App">
        <header className="header">
          <div className="header-content">
            <h1>OrderApp</h1>
          </div>
        </header>
        <main className="main">
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <h2>Yükleniyor...</h2>
          </div>
        </main>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="App">
        <header className="header">
          <div className="header-content">
            <h1>OrderApp</h1>
          </div>
        </header>
        <main className="main">
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <h2>Hata</h2>
            <p>{state.error}</p>
            <button onClick={loadEssentialData} className="add-button">
              Tekrar Dene
            </button>
            <button
              onClick={() => {
                apiService.removeAuthToken();
                window.location.reload();
              }}
              className="action-button delete"
              style={{ marginLeft: "1rem" }}
            >
              Çıkış Yap
            </button>
          </div>
        </main>
      </div>
    );
  }

  const unreadNotifications = Array.isArray(state.notifications)
    ? state.notifications.filter((n: any) => !n.isRead)?.length || 0
    : 0;

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              title="Menüyü aç/kapat"
            >
              ☰
            </button>
          </div>
          <div className="header-center">
            <div className="welcome-message">
              <h2>Hoş Geldiniz {state.user?.name || "User"}!</h2>
              <p>
                Bugün{" "}
                {new Date().toLocaleDateString("tr-TR", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  weekday: "long",
                })}
              </p>
            </div>
          </div>
          <div className="header-right">
            <button
              className="theme-toggle-btn"
              onClick={toggleTheme}
              title={theme === "light" ? "Koyu moda geç" : "Açık moda geç"}
            >
              {theme === "light" ? "🌙" : "☀️"}
            </button>
            <div className="user-profile">
              <div className="user-avatar">
                {state.user?.name?.charAt(0) || "U"}
              </div>
              <div className="user-details">
                <span className="user-name">
                  {state.user?.name || "Kullanıcı"}
                </span>
                <span className="user-role">
                  {state.user?.role === "admin"
                    ? "Admin"
                    : state.user?.role === "manager"
                      ? "Müdür"
                      : state.user?.role === "warehouse"
                        ? "Depo"
                        : "Satış Temsilcisi"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar Overlay for Mobile */}
      {window.innerWidth <= 1024 && (
        <div
          className={`sidebar-overlay ${sidebarOpen ? "active" : ""}`}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-header">
          <h2>OrderApp</h2>
          <button
            className="sidebar-close"
            onClick={() => setSidebarOpen(false)}
          >
            ✕
          </button>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`sidebar-nav-item ${state.activeTab === "dashboard" ? "active" : ""}`}
            onClick={() => {
              dispatch({ type: "SET_ACTIVE_TAB", payload: "dashboard" });
            }}
          >
            <span className="nav-icon">📊</span>
            <span className="nav-text">Dashboard</span>
          </button>

          <button
            className={`sidebar-nav-item ${state.activeTab === "orders" ? "active" : ""}`}
            onClick={() => {
              dispatch({ type: "SET_ACTIVE_TAB", payload: "orders" });
            }}
          >
            <span className="nav-icon">📦</span>
            <span className="nav-text">Siparişler</span>
            {Array.isArray(state.orders) &&
              state.orders.filter((o) => o.status === "pending_approval")
                .length > 0 && (
                <span className="nav-badge">
                  {Array.isArray(state.orders)
                    ? state.orders.filter(
                        (o) => o.status === "pending_approval"
                      )?.length || 0
                    : 0}
                </span>
              )}
          </button>

          {state.user && canAddCustomer(state.user.role) && (
            <button
              className={`sidebar-nav-item ${state.activeTab === "customers" ? "active" : ""}`}
              onClick={() => {
                dispatch({ type: "SET_ACTIVE_TAB", payload: "customers" });
              }}
            >
              <span className="nav-icon">👥</span>
              <span className="nav-text">Müşteriler</span>
            </button>
          )}

          {state.user && canAddProduct(state.user.role) && (
            <button
              className={`sidebar-nav-item ${state.activeTab === "products" ? "active" : ""}`}
              onClick={() => {
                dispatch({ type: "SET_ACTIVE_TAB", payload: "products" });
              }}
            >
              <span className="nav-icon">🏷️</span>
              <span className="nav-text">Ürünler</span>
            </button>
          )}

          {state.user && canViewAnalytics(state.user.role) && (
            <button
              className={`sidebar-nav-item ${state.activeTab === "analytics" ? "active" : ""}`}
              onClick={() => {
                dispatch({ type: "SET_ACTIVE_TAB", payload: "analytics" });
              }}
            >
              <span className="nav-icon">📈</span>
              <span className="nav-text">Raporlar</span>
            </button>
          )}

          {state.user && canAddWarehouse(state.user.role) && (
            <button
              className={`sidebar-nav-item ${state.activeTab === "warehouses" ? "active" : ""}`}
              onClick={() => {
                dispatch({ type: "SET_ACTIVE_TAB", payload: "warehouses" });
              }}
            >
              <span className="nav-icon">🏢</span>
              <span className="nav-text">Depolar</span>
            </button>
          )}

          <button
            className={`sidebar-nav-item ${state.activeTab === "notifications" ? "active" : ""}`}
            onClick={() => {
              dispatch({ type: "SET_ACTIVE_TAB", payload: "notifications" });
            }}
          >
            <span className="nav-icon">🔔</span>
            <span className="nav-text">Bildirimler</span>
            {unreadNotifications > 0 && (
              <span className="nav-badge">{unreadNotifications}</span>
            )}
          </button>

          <button
            className={`sidebar-nav-item ${state.activeTab === "settings" ? "active" : ""}`}
            onClick={() => {
              dispatch({ type: "SET_ACTIVE_TAB", payload: "settings" });
            }}
          >
            <span className="nav-icon">⚙️</span>
            <span className="nav-text">Ayarlar</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button
            className="sidebar-nav-item logout-button"
            onClick={() => {
              apiService.removeAuthToken();
              window.location.reload();
            }}
          >
            <span className="nav-icon">🚪</span>
            <span className="nav-text">Çıkış Yap</span>
          </button>
        </div>
      </div>

      <main
        className={`main ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
      >
        {state.activeTab === "dashboard" && (
          <div className="dashboard-section">
            <h2>OrderApp Dashboard</h2>

            {/* Summary Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Toplam Sipariş</h3>
                <div className="stat-value">
                  <div className="stat-icon orange">📦</div>
                  {Array.isArray(state.orders) ? state.orders.length : 0}
                </div>
                <div className="stat-trend up">
                  ↑{" "}
                  {Array.isArray(state.orders)
                    ? Math.floor(state.orders.length * 0.15)
                    : 0}{" "}
                  bu ay
                </div>
              </div>

              <div className="stat-card">
                <h3>Onay Bekleyen</h3>
                <div className="stat-value">
                  <div className="stat-icon purple">⏳</div>
                  {Array.isArray(state.orders)
                    ? state.orders.filter(
                        (order) => order.status === "pending_approval"
                      ).length
                    : 0}
                </div>
                <div className="stat-trend neutral">Beklemede</div>
              </div>

              <div className="stat-card">
                <h3>Hazırlanan</h3>
                <div className="stat-value">
                  <div className="stat-icon blue">🔧</div>
                  {Array.isArray(state.orders)
                    ? state.orders.filter(
                        (order) => order.status === "preparing"
                      ).length
                    : 0}
                </div>
                <div className="stat-trend up">↑ Aktif</div>
              </div>

              <div className="stat-card">
                <h3>Toplam Gelir</h3>
                <div className="stat-value">
                  <div className="stat-icon green">💰</div>₺
                  {Array.isArray(state.orders)
                    ? state.orders
                        .filter((order) => order.status !== "cancelled")
                        .reduce(
                          (total, order) => total + (order.grandTotal || 0),
                          0
                        )
                        .toLocaleString()
                    : 0}
                </div>
                <div className="stat-trend up">↑ %21 geçen aya göre</div>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="charts-grid">
              <div className="chart-container">
                <div className="chart-header">
                  <h3>Sipariş Durumu Dağılımı</h3>
                  <button className="chart-menu">⋯</button>
                </div>
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f8fafc",
                    borderRadius: "8px",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                      📊
                    </div>
                    <p style={{ color: "#64748b" }}>Grafik yükleniyor...</p>
                  </div>
                </div>
              </div>

              <div className="chart-container">
                <div className="chart-header">
                  <h3>Aylık Gelir Özeti</h3>
                  <button className="chart-menu">⋯</button>
                </div>
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f8fafc",
                    borderRadius: "8px",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                      📈
                    </div>
                    <p style={{ color: "#64748b" }}>Grafik yükleniyor...</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tables Grid */}
            <div className="tables-grid">
              <div className="table-container">
                <div className="table-header">
                  <h3>Son Siparişler</h3>
                  <button className="chart-menu">⋯</button>
                </div>
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Müşteri</th>
                      <th>Tutar</th>
                      <th>Durum</th>
                      <th>Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(state.orders) && state.orders.length > 0 ? (
                      state.orders
                        .sort(
                          (a, b) =>
                            new Date(
                              b.createdAt || b.created_at || 0
                            ).getTime() -
                            new Date(a.createdAt || a.created_at || 0).getTime()
                        )
                        .slice(0, 5)
                        .map((order, index) => (
                          <tr key={order.id}>
                            <td>{index + 1}</td>
                            <td>
                              {order.customer?.name ||
                                order.customerName ||
                                order.customerEmail ||
                                "Bilinmeyen"}
                            </td>
                            <td>
                              ₺
                              {(
                                order.grandTotal ||
                                order.total_amount ||
                                0
                              ).toLocaleString()}
                            </td>
                            <td>
                              <span
                                className={`status-badge ${order.status === "pending_approval" ? "pending" : order.status === "delivered" ? "approved" : "rejected"}`}
                              >
                                {order.status === "pending_approval"
                                  ? "Beklemede"
                                  : order.status === "approved"
                                    ? "Onaylandı"
                                    : order.status === "preparing"
                                      ? "Hazırlanıyor"
                                      : order.status === "in_transit"
                                        ? "Yolda"
                                        : order.status === "delivered"
                                          ? "Teslim Edildi"
                                          : order.status === "cancelled"
                                            ? "İptal"
                                            : order.status}
                              </span>
                            </td>
                            <td>
                              {order.createdAt || order.created_at
                                ? new Date(
                                    order.createdAt || order.created_at!
                                  ).toLocaleDateString("tr-TR")
                                : "Tarih yok"}
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            textAlign: "center",
                            padding: "2rem",
                            color: "#64748b",
                          }}
                        >
                          Henüz sipariş bulunmuyor.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="table-container">
                <div className="table-header">
                  <h3>Bütçe Geçmişi</h3>
                  <button className="chart-menu">⋯</button>
                </div>
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Bütçe No.</th>
                      <th>Planlanan (₺)</th>
                      <th>Gerçekleşen (₺)</th>
                      <th>Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>1</td>
                      <td>BGT-2025-001</td>
                      <td>50,000</td>
                      <td>48,500</td>
                      <td>2025-01-15</td>
                    </tr>
                    <tr>
                      <td>2</td>
                      <td>BGT-2025-002</td>
                      <td>75,000</td>
                      <td>72,300</td>
                      <td>2025-02-01</td>
                    </tr>
                    <tr>
                      <td>3</td>
                      <td>BGT-2025-003</td>
                      <td>100,000</td>
                      <td>98,750</td>
                      <td>2025-03-01</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {state.activeTab === "orders" && (
          <div className="orders-section">
            <div className="section-header">
              <h2>Siparişler</h2>
              {(state.user?.role === "salesperson" ||
                state.user?.role === "admin" ||
                state.user?.role === "manager") && (
                <button
                  className="add-button"
                  onClick={() => {
                    setNewOrder({
                      customerId: "",
                      customerName: "",
                      items: [],
                      notes: "",
                    });
                    setShowOrderModal(true);
                  }}
                >
                  + Yeni Sipariş
                </button>
              )}
            </div>

            {/* Orders Tab Navigation */}
            <div className="orders-tabs">
              <button
                className={`tab-button ${ordersTab === "active" ? "active" : ""}`}
                onClick={() => setOrdersTab("active")}
              >
                <span>📦</span>
                <span>Aktif Siparişler</span>
                <span className="tab-count">{activeOrdersCount}</span>
              </button>
              <button
                className={`tab-button ${ordersTab === "pending_approval" ? "active" : ""}`}
                onClick={() => setOrdersTab("pending_approval")}
              >
                <span>⏳</span>
                <span>Onay Bekliyor</span>
                <span className="tab-count">{pendingOrdersCount}</span>
              </button>
              <button
                className={`tab-button ${ordersTab === "cancelled" ? "active" : ""}`}
                onClick={() => setOrdersTab("cancelled")}
              >
                <span>❌</span>
                <span>İptal Edilen</span>
                <span className="tab-count">{cancelledOrdersCount}</span>
              </button>
            </div>

            <div className="orders-grid">
              {getFilteredOrdersByTab().length > 0 ? (
                getFilteredOrdersByTab().map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    currentUser={state.user!}
                    onOrderAction={handleOrderActionWrapper}
                  />
                ))
              ) : (
                <div className="no-orders">
                  <p>
                    {ordersTab === "active"
                      ? "Henüz aktif sipariş bulunmuyor."
                      : ordersTab === "pending_approval"
                        ? "Henüz onay bekleyen sipariş bulunmuyor."
                        : "Henüz iptal edilmiş sipariş bulunmuyor."}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {state.activeTab === "customers" && (
          <div className="customers-section">
            <div className="section-header">
              <h2>Müşteriler</h2>
              {(state.user?.role === "salesperson" ||
                state.user?.role === "admin" ||
                state.user?.role === "manager") && (
                <button
                  className="add-button"
                  onClick={() => setShowCustomerModal(true)}
                >
                  + Yeni Müşteri
                </button>
              )}
            </div>

            <div className="customers-grid">
              {loadingCustomers ? (
                <div className="loading-customers">
                  <p>Müşteriler yükleniyor...</p>
                </div>
              ) : customers && customers.length > 0 ? (
                customers.map((customer) => (
                  <div key={customer.id} className="customer-card">
                    <div className="customer-header">
                      <h3>
                        {customer.name ||
                          customer.full_name ||
                          customer.company_name ||
                          "İsimsiz Müşteri"}
                      </h3>
                      <span className="customer-status active">Aktif</span>
                    </div>
                    <div className="customer-details">
                      <p>
                        <strong>E-posta:</strong>{" "}
                        {customer.email || "Belirtilmemiş"}
                      </p>
                      <p>
                        <strong>Telefon:</strong>{" "}
                        {customer.phone || "Belirtilmemiş"}
                      </p>
                    </div>
                    <div className="customer-actions">
                      <button className="action-button edit">Düzenle</button>
                      <button className="action-button delete">Sil</button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-customers">
                  <p>
                    Henüz müşteri bulunmuyor. Yeni müşteri ekleyerek başlayın.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {state.activeTab === "products" && (
          <div className="products-section">
            <div className="section-header">
              <h2>Ürünler</h2>
              {state.user && canAddProduct(state.user.role) && (
                <button
                  className="add-button"
                  onClick={() => setShowProductModal(true)}
                  title={
                    state.user.role === "admin"
                      ? "Admin"
                      : state.user.role === "manager"
                        ? "Müdür"
                        : state.user.role === "warehouse"
                          ? "Depo"
                          : "Satış Temsilcisi"
                  }
                >
                  + Yeni Ürün
                </button>
              )}
            </div>
            <div className="products-grid">
              <div className="no-products">
                <p>Henüz ürün bulunmuyor. Yeni ürün ekleyerek başlayın.</p>
              </div>
            </div>
          </div>
        )}

        {state.activeTab === "warehouses" && (
          <div className="warehouses-section">
            <div className="section-header">
              <h2>Depo Yönetimi</h2>
              {state.user && canAddWarehouse(state.user.role) && (
                <button
                  className="add-button"
                  onClick={() => setShowWarehouseModal(true)}
                  title={
                    state.user.role === "admin"
                      ? "Admin"
                      : state.user.role === "manager"
                        ? "Müdür"
                        : state.user.role === "warehouse"
                          ? "Depo"
                          : "Satış Temsilcisi"
                  }
                >
                  + Yeni Depo
                </button>
              )}
            </div>

            {/* Warehouse Summary Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Toplam Depo</h3>
                <div className="stat-value">
                  <div className="stat-icon blue">🏢</div>
                  {state.warehouses ? state.warehouses.length : 0}
                </div>
                <div className="stat-trend up">↑ Aktif</div>
              </div>

              <div className="stat-card">
                <h3>Ortalama Doluluk</h3>
                <div className="stat-value">
                  <div className="stat-icon green">📊</div>
                  {state.warehouses && state.warehouses.length > 0
                    ? Math.round(
                        state.warehouses.reduce((acc, warehouse) => {
                          const usage =
                            warehouse.capacity > 0
                              ? (warehouse.current_occupancy /
                                  warehouse.capacity) *
                                100
                              : 0;
                          return acc + usage;
                        }, 0) / state.warehouses.length
                      )
                    : 0}
                  %
                </div>
                <div className="stat-trend neutral">Kapasite</div>
              </div>

              <div className="stat-card">
                <h3>Toplam Kapasite</h3>
                <div className="stat-value">
                  <div className="stat-icon purple">📦</div>
                  {state.warehouses
                    ? state.warehouses.reduce(
                        (acc, warehouse) => acc + (warehouse.capacity || 0),
                        0
                      )
                    : 0}
                </div>
                <div className="stat-trend up">↑ Birim</div>
              </div>

              <div className="stat-card">
                <h3>Kritik Stok</h3>
                <div className="stat-value">
                  <div className="stat-icon orange">⚠️</div>
                  {state.warehouses
                    ? state.warehouses.filter((warehouse) => {
                        const usage =
                          warehouse.capacity > 0
                            ? (warehouse.current_occupancy /
                                warehouse.capacity) *
                              100
                            : 0;
                        return usage > 90;
                      }).length
                    : 0}
                </div>
                <div className="stat-trend down">↓ Dikkat</div>
              </div>
            </div>

            {/* Warehouse Charts Grid */}
            <div className="charts-grid">
              <div className="chart-container">
                <div className="chart-header">
                  <h3>Depo Doluluk Oranları</h3>
                  <button className="chart-menu">⋯</button>
                </div>
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f8fafc",
                    borderRadius: "8px",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                      📈
                    </div>
                    <p style={{ color: "#64748b" }}>
                      Doluluk grafiği yükleniyor...
                    </p>
                  </div>
                </div>
              </div>

              <div className="chart-container">
                <div className="chart-header">
                  <h3>Depo Performans Analizi</h3>
                  <button className="chart-menu">⋯</button>
                </div>
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f8fafc",
                    borderRadius: "8px",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                      📊
                    </div>
                    <p style={{ color: "#64748b" }}>
                      Performans grafiği yükleniyor...
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Warehouse Management Table */}
            <div className="table-container">
              <div className="table-header">
                <h3>Depo Detayları</h3>
                <button className="chart-menu">⋯</button>
              </div>
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Depo Adı</th>
                    <th>Kod</th>
                    <th>Tip</th>
                    <th>Kapasite</th>
                    <th>Doluluk</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {state.warehouses && state.warehouses.length > 0 ? (
                    state.warehouses.map((warehouse: any) => {
                      const usagePercentage =
                        warehouse.capacity > 0
                          ? Math.round(
                              (warehouse.current_occupancy /
                                warehouse.capacity) *
                                100
                            )
                          : 0;

                      return (
                        <tr key={warehouse.id}>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                              }}
                            >
                              <span style={{ fontSize: "1.2rem" }}>🏢</span>
                              <strong>{warehouse.warehouse_name}</strong>
                            </div>
                          </td>
                          <td>{warehouse.warehouse_code}</td>
                          <td>{warehouse.warehouse_type}</td>
                          <td>{warehouse.capacity?.toLocaleString() || 0}</td>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                              }}
                            >
                              <div
                                style={{
                                  width: "60px",
                                  height: "8px",
                                  background: "#e2e8f0",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    width: `${usagePercentage}%`,
                                    height: "100%",
                                    background:
                                      usagePercentage > 90
                                        ? "#ef4444"
                                        : usagePercentage > 70
                                          ? "#f59e0b"
                                          : "#10b981",
                                    transition: "width 0.3s ease",
                                  }}
                                ></div>
                              </div>
                              <span
                                style={{ fontSize: "0.9rem", color: "#64748b" }}
                              >
                                {usagePercentage}%
                              </span>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`status-badge ${warehouse.status === "active" ? "approved" : warehouse.status === "maintenance" ? "pending" : "rejected"}`}
                            >
                              {warehouse.status === "active"
                                ? "Aktif"
                                : warehouse.status === "maintenance"
                                  ? "Bakım"
                                  : "Pasif"}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                              <button
                                style={{
                                  padding: "0.25rem 0.5rem",
                                  background: "#f1f5f9",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "4px",
                                  fontSize: "0.8rem",
                                  cursor: "pointer",
                                }}
                              >
                                📋 Detay
                              </button>
                              <button
                                style={{
                                  padding: "0.25rem 0.5rem",
                                  background: "#fef3c7",
                                  border: "1px solid #f59e0b",
                                  borderRadius: "4px",
                                  fontSize: "0.8rem",
                                  cursor: "pointer",
                                }}
                              >
                                ✏️ Düzenle
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        style={{
                          textAlign: "center",
                          padding: "2rem",
                          color: "#64748b",
                        }}
                      >
                        Henüz depo bulunmuyor. Yeni depo ekleyerek başlayın.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {state.activeTab === "analytics" &&
          (state.user?.role === "admin" || state.user?.role === "manager") && (
            <AnalyticsDashboard
              data={{
                salesData: [
                  { month: "Ocak", sales: 12000 },
                  { month: "Şubat", sales: 15000 },
                  { month: "Mart", sales: 18000 },
                  { month: "Nisan", sales: 22000 },
                  { month: "Mayıs", sales: 25000 },
                  { month: "Haziran", sales: 28000 },
                ],
                categoryData: [
                  { category: "İlaç", sales: 45000 },
                  { category: "Tıbbi Cihaz", sales: 32000 },
                  { category: "Kozmetik", sales: 28000 },
                  { category: "Vitamin", sales: 22000 },
                  { category: "Diğer", sales: 15000 },
                ],
                customerData: [
                  { customer: "Ahmet Yılmaz", orders: 15, total: 45000 },
                  { customer: "Fatma Demir", orders: 12, total: 38000 },
                  { customer: "Mehmet Kaya", orders: 10, total: 32000 },
                  { customer: "Ayşe Özkan", orders: 8, total: 28000 },
                  { customer: "Ali Çelik", orders: 6, total: 22000 },
                ],
                statusData: [
                  { status: "Onay Bekleyen", count: 5 },
                  { status: "Onaylandı", count: 12 },
                  { status: "Hazırlanıyor", count: 8 },
                  { status: "Teslim Edildi", count: 25 },
                ],
                // Yeni eklenen veriler
                regionalData: [
                  {
                    region: "İstanbul",
                    sales: 85000,
                    orders: 45,
                    customers: 28,
                  },
                  { region: "Ankara", sales: 62000, orders: 32, customers: 22 },
                  { region: "İzmir", sales: 48000, orders: 25, customers: 18 },
                  { region: "Bursa", sales: 35000, orders: 18, customers: 12 },
                  {
                    region: "Antalya",
                    sales: 28000,
                    orders: 15,
                    customers: 10,
                  },
                  { region: "Adana", sales: 22000, orders: 12, customers: 8 },
                  { region: "Konya", sales: 18000, orders: 10, customers: 6 },
                ],
                personnelData: [
                  {
                    name: "Mehmet Yönetici",
                    role: "Manager",
                    performance: 95,
                    orders: 45,
                  },
                  {
                    name: "Ayşe Satış",
                    role: "Salesperson",
                    performance: 88,
                    orders: 38,
                  },
                  {
                    name: "Ali Depo",
                    role: "Warehouse",
                    performance: 92,
                    orders: 42,
                  },
                  {
                    name: "Fatma Sürücü",
                    role: "Driver",
                    performance: 85,
                    orders: 35,
                  },
                  {
                    name: "Ahmet Admin",
                    role: "Admin",
                    performance: 98,
                    orders: 50,
                  },
                ],
                warehouseData: [
                  {
                    warehouse: "İstanbul Ana Depo",
                    capacity: 1000,
                    usage: 85,
                    products: 450,
                  },
                  {
                    warehouse: "Ankara Depo",
                    capacity: 800,
                    usage: 72,
                    products: 320,
                  },
                  {
                    warehouse: "İzmir Depo",
                    capacity: 600,
                    usage: 68,
                    products: 280,
                  },
                  {
                    warehouse: "Bursa Soğuk Zincir",
                    capacity: 400,
                    usage: 45,
                    products: 180,
                  },
                  {
                    warehouse: "Antalya Depo",
                    capacity: 300,
                    usage: 38,
                    products: 120,
                  },
                ],
                timeSeriesData: [
                  { date: "01/01", sales: 12000, orders: 15, customers: 8 },
                  { date: "01/02", sales: 13500, orders: 18, customers: 10 },
                  { date: "01/03", sales: 14200, orders: 20, customers: 12 },
                  { date: "01/04", sales: 15800, orders: 22, customers: 14 },
                  { date: "01/05", sales: 16500, orders: 25, customers: 16 },
                  { date: "01/06", sales: 17800, orders: 28, customers: 18 },
                  { date: "01/07", sales: 18500, orders: 30, customers: 20 },
                  { date: "01/08", sales: 19200, orders: 32, customers: 22 },
                  { date: "01/09", sales: 20500, orders: 35, customers: 24 },
                  { date: "01/10", sales: 21800, orders: 38, customers: 26 },
                  { date: "01/11", sales: 22500, orders: 40, customers: 28 },
                  { date: "01/12", sales: 23800, orders: 42, customers: 30 },
                ],
              }}
            />
          )}

        {state.activeTab === "notifications" && (
          <div className="notifications-section">
            <h2>Bildirimler</h2>
            <div className="notifications-list">
              {state.notifications?.map((notification: any) => (
                <div
                  key={notification.id}
                  className={`notification-item ${notification.isRead ? "read" : "unread"}`}
                  onClick={async () => {
                    if (!notification.isRead) {
                      try {
                        await apiService.markNotificationAsRead(
                          notification.id
                        );
                        // Update local state
                        dispatch({
                          type: "UPDATE_NOTIFICATION",
                          payload: { id: notification.id, isRead: true },
                        });
                      } catch (error) {
                        console.error(
                          "Error marking notification as read:",
                          error
                        );
                      }
                    }
                  }}
                >
                  <div className="notification-header">
                    <h4>{notification.title}</h4>
                    <span className="notification-time">
                      {new Date(notification.createdAt).toLocaleString("tr-TR")}
                    </span>
                  </div>
                  <p>{notification.message}</p>
                  {!notification.isRead && (
                    <div className="notification-badge">Yeni</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {state.activeTab === "settings" && state.settings && (
          <div className="settings-section">
            <h2>Ayarlar</h2>
            <div className="settings-form">
              <div className="form-group">
                <label>Şirket Adı</label>
                <input
                  type="text"
                  placeholder="Şirket adınızı girin"
                  value={state.settings.companyName}
                  onChange={(e) =>
                    dispatch({
                      type: "SET_SETTINGS",
                      payload: {
                        ...state.settings,
                        companyName: e.target.value,
                      },
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label>E-posta</label>
                <input
                  type="email"
                  placeholder="E-posta adresinizi girin"
                  value={state.settings.email}
                  onChange={(e) =>
                    dispatch({
                      type: "SET_SETTINGS",
                      payload: { ...state.settings, email: e.target.value },
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label>Telefon</label>
                <input
                  type="tel"
                  placeholder="Telefon numaranızı girin"
                  value={state.settings.phone}
                  onChange={(e) =>
                    dispatch({
                      type: "SET_SETTINGS",
                      payload: { ...state.settings, phone: e.target.value },
                    })
                  }
                />
              </div>
              <button
                className="save-button"
                onClick={() =>
                  state.settings && apiService.updateSettings(state.settings)
                }
              >
                Kaydet
              </button>
            </div>
          </div>
        )}

        {/* Order Creation Modal */}
        {showOrderModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowOrderModal(false)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Yeni Sipariş Oluştur</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowOrderModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>
                    Müşteri Seçin * (Debug: {customers?.length || 0} müşteri)
                  </label>
                  <select
                    value={newOrder.customerId}
                    onChange={(e) => {
                      console.log("🎯 Müşteri seçimi:", e.target.value);
                      const selectedCustomer = customers.find(
                        (c) => c.id == e.target.value
                      );
                      console.log("👤 Seçilen müşteri:", selectedCustomer);
                      setNewOrder({
                        ...newOrder,
                        customerId: e.target.value,
                        customerName:
                          selectedCustomer?.name ||
                          selectedCustomer?.full_name ||
                          selectedCustomer?.company_name ||
                          "",
                      });
                    }}
                    required
                  >
                    <option value="">Müşteri seçin</option>
                    {loadingCustomers ? (
                      <option disabled>Müşteriler yükleniyor...</option>
                    ) : customers && customers.length > 0 ? (
                      customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name ||
                            customer.full_name ||
                            customer.company_name}{" "}
                          - {customer.email || customer.phone}
                        </option>
                      ))
                    ) : (
                      <option disabled>Müşteri bulunamadı</option>
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label>Ürünler (Debug: {products?.length || 0} ürün)</label>

                  {/* Seçili Ürünler */}
                  {newOrder.items
                    .filter((item: any) => item.productId)
                    .map((item: any, index: number) => {
                      const selectedProduct = products.find(
                        (p) => p.id == item.productId
                      );
                      return (
                        <div key={index} className="selected-product-card">
                          <div className="product-image">
                            <img
                              src={
                                selectedProduct?.imageUrl ||
                                `https://picsum.photos/200/200?random=${selectedProduct?.id || Math.random()}`
                              }
                              alt={selectedProduct?.name || "Ürün"}
                              onError={(e) => {
                                e.currentTarget.src = `https://picsum.photos/200/200?random=${selectedProduct?.id || Math.random()}`;
                              }}
                            />
                          </div>
                          <div className="product-details">
                            <h4>{selectedProduct?.name || "Ürün seçin"}</h4>
                            <p className="product-sku">
                              SKU:{" "}
                              {selectedProduct?.product_code ||
                                selectedProduct?.sku ||
                                "N/A"}
                            </p>
                            <p className="product-price">
                              Birim Fiyat: <strong>{item.unitPrice}₺</strong>
                            </p>
                          </div>
                          <div className="product-quantity">
                            <label>Miktar:</label>
                            <div className="quantity-controls">
                              <button
                                type="button"
                                onClick={() => {
                                  if (item.quantity > 1) {
                                    const updatedItems: any[] = [
                                      ...newOrder.items,
                                    ];
                                    updatedItems[index] = {
                                      ...updatedItems[index],
                                      quantity: item.quantity - 1,
                                    };
                                    setNewOrder({
                                      ...newOrder,
                                      items: updatedItems,
                                    });
                                  }
                                }}
                                className="quantity-btn"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => {
                                  const updatedItems: any[] = [
                                    ...newOrder.items,
                                  ];
                                  updatedItems[index] = {
                                    ...updatedItems[index],
                                    quantity: parseInt(e.target.value) || 1,
                                  };
                                  setNewOrder({
                                    ...newOrder,
                                    items: updatedItems,
                                  });
                                }}
                                className="quantity-input"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedItems: any[] = [
                                    ...newOrder.items,
                                  ];
                                  updatedItems[index] = {
                                    ...updatedItems[index],
                                    quantity: item.quantity + 1,
                                  };
                                  setNewOrder({
                                    ...newOrder,
                                    items: updatedItems,
                                  });
                                }}
                                className="quantity-btn"
                              >
                                +
                              </button>
                            </div>
                            <p className="total-price">
                              Toplam:{" "}
                              <strong>
                                {(
                                  item.quantity * item.unitPrice
                                ).toLocaleString()}
                                ₺
                              </strong>
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedItems = newOrder.items.filter(
                                (_: any, i: number) => i !== index
                              );
                              setNewOrder({ ...newOrder, items: updatedItems });
                            }}
                            className="remove-product-btn"
                          >
                            🗑️
                          </button>
                        </div>
                      );
                    })}

                  {/* Ürün Seçimi */}
                  <div className="product-selection">
                    <h4>Ürün Ekle</h4>
                    <div className="product-grid">
                      {loadingProducts ? (
                        <div className="loading-products">
                          Ürünler yükleniyor...
                        </div>
                      ) : products && products.length > 0 ? (
                        products.map((product) => {
                          const isSelected = newOrder.items.some(
                            (item: any) => item.productId === product.id
                          );
                          return (
                            <div
                              key={product.id}
                              className={`product-card ${isSelected ? "selected" : ""}`}
                              onClick={() => {
                                if (!isSelected) {
                                  setNewOrder({
                                    ...newOrder,
                                    items: [
                                      ...newOrder.items,
                                      {
                                        productId: product.id.toString(),
                                        productName: product.name,
                                        quantity: 1,
                                        unitPrice:
                                          product.unit_price ||
                                          product.price ||
                                          0,
                                      },
                                    ],
                                  });
                                }
                              }}
                            >
                              <div className="product-card-image">
                                <img
                                  src={
                                    product.imageUrl ||
                                    `https://picsum.photos/200/200?random=${product.id}`
                                  }
                                  alt={product.name}
                                  onError={(e) => {
                                    e.currentTarget.src = `https://picsum.photos/200/200?random=${product.id}`;
                                  }}
                                />
                                {isSelected && (
                                  <div className="selected-badge">✓</div>
                                )}
                              </div>
                              <div className="product-card-info">
                                <h5>{product.name}</h5>
                                <p className="product-sku">
                                  SKU:{" "}
                                  {product.product_code || product.sku || "N/A"}
                                </p>
                                <p className="product-price">
                                  {product.unit_price || product.price}₺
                                </p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="no-products">Ürün bulunamadı</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Notlar</label>
                  <textarea
                    value={newOrder.notes}
                    onChange={(e) =>
                      setNewOrder({ ...newOrder, notes: e.target.value })
                    }
                    placeholder="Sipariş notları..."
                    rows={3}
                  />
                </div>
                <div className="modal-actions">
                  <button
                    className="action-button delete"
                    onClick={() => setShowOrderModal(false)}
                  >
                    İptal
                  </button>
                  <button
                    className="action-button edit"
                    onClick={handleCreateOrder}
                  >
                    Sipariş Oluştur
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer Creation Modal */}
        {showCustomerModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowCustomerModal(false)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Yeni Müşteri Oluştur</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowCustomerModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Müşteri Adı *</label>
                  <input
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, name: e.target.value })
                    }
                    placeholder="Müşteri adını girin"
                  />
                </div>
                <div className="form-group">
                  <label>E-posta *</label>
                  <input
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, email: e.target.value })
                    }
                    placeholder="E-posta adresini girin"
                  />
                </div>
                <div className="form-group">
                  <label>Telefon</label>
                  <input
                    type="tel"
                    value={newCustomer.phone}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, phone: e.target.value })
                    }
                    placeholder="Telefon numarasını girin"
                  />
                </div>
                <div className="form-group">
                  <label>Adres</label>
                  <textarea
                    value={newCustomer.address}
                    onChange={(e) =>
                      setNewCustomer({
                        ...newCustomer,
                        address: e.target.value,
                      })
                    }
                    placeholder="Adres bilgilerini girin"
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>Şirket Adı</label>
                  <input
                    type="text"
                    value={newCustomer.companyName}
                    onChange={(e) =>
                      setNewCustomer({
                        ...newCustomer,
                        companyName: e.target.value,
                      })
                    }
                    placeholder="Şirket adını girin"
                  />
                </div>
                <div className="form-group">
                  <label>Vergi Numarası (VKN)</label>
                  <input
                    type="text"
                    value={newCustomer.taxNumber}
                    onChange={(e) =>
                      setNewCustomer({
                        ...newCustomer,
                        taxNumber: e.target.value,
                      })
                    }
                    placeholder="Vergi kimlik numarasını girin"
                  />
                </div>
                <div className="form-group">
                  <label>Vergi Dairesi</label>
                  <input
                    type="text"
                    value={newCustomer.taxOffice}
                    onChange={(e) =>
                      setNewCustomer({
                        ...newCustomer,
                        taxOffice: e.target.value,
                      })
                    }
                    placeholder="Vergi dairesini girin"
                  />
                </div>
                <div className="form-group">
                  <label>Web Sitesi</label>
                  <input
                    type="url"
                    value={newCustomer.website}
                    onChange={(e) =>
                      setNewCustomer({
                        ...newCustomer,
                        website: e.target.value,
                      })
                    }
                    placeholder="https://www.example.com"
                  />
                </div>
                <div className="modal-actions">
                  <button
                    className="action-button delete"
                    onClick={() => setShowCustomerModal(false)}
                  >
                    İptal
                  </button>
                  <button
                    className="action-button edit"
                    onClick={handleCreateCustomer}
                  >
                    Müşteri Oluştur
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product Creation Modal */}
        {showProductModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowProductModal(false)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Yeni Ürün Oluştur</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowProductModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, name: e.target.value })
                    }
                    placeholder="Ürün adını girin"
                  />
                </div>
                <div className="form-group">
                  <label>Ürün Kodu *</label>
                  <input
                    type="text"
                    value={newProduct.productCode}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        productCode: e.target.value,
                      })
                    }
                    placeholder="Ürün kodunu girin"
                  />
                </div>
                <div className="form-group">
                  <label>Açıklama</label>
                  <textarea
                    value={newProduct.description}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        description: e.target.value,
                      })
                    }
                    placeholder="Ürün açıklamasını girin"
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>Birim Fiyatı (₺) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newProduct.unitPrice}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        unitPrice: parseFloat(e.target.value) || 0,
                      })
                    }
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group">
                  <label>KDV Oranı (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={newProduct.taxRate}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        taxRate: parseInt(e.target.value) || 18,
                      })
                    }
                    placeholder="18"
                  />
                </div>
                <div className="form-group">
                  <label>Stok Miktarı</label>
                  <input
                    type="number"
                    min="0"
                    value={newProduct.stockQuantity}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        stockQuantity: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Birim</label>
                  <select
                    value={newProduct.unit}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, unit: e.target.value })
                    }
                  >
                    <option value="adet">Adet</option>
                    <option value="kg">Kilogram</option>
                    <option value="litre">Litre</option>
                    <option value="metre">Metre</option>
                    <option value="paket">Paket</option>
                    <option value="kutu">Kutu</option>
                    <option value="cift">Çift</option>
                    <option value="set">Set</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Depolar *</label>
                  <div className="warehouse-selection">
                    {loadingWarehouses ? (
                      <p>Depolar yükleniyor...</p>
                    ) : warehouses.length > 0 ? (
                      <div className="warehouse-checkboxes">
                        {warehouses.map((warehouse) => (
                          <label
                            key={warehouse.id}
                            className="warehouse-checkbox"
                          >
                            <input
                              type="checkbox"
                              checked={newProduct.selectedWarehouses.includes(
                                warehouse.id
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewProduct({
                                    ...newProduct,
                                    selectedWarehouses: [
                                      ...newProduct.selectedWarehouses,
                                      warehouse.id,
                                    ],
                                  });
                                } else {
                                  setNewProduct({
                                    ...newProduct,
                                    selectedWarehouses:
                                      newProduct.selectedWarehouses.filter(
                                        (id) => id !== warehouse.id
                                      ),
                                  });
                                }
                              }}
                            />
                            <span>
                              {warehouse.ad} - {warehouse.il}/{warehouse.ilce}
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p>Henüz depo bulunmuyor. Önce depo oluşturun.</p>
                    )}
                  </div>
                </div>
                <div className="modal-actions">
                  <button
                    className="action-button delete"
                    onClick={() => setShowProductModal(false)}
                  >
                    İptal
                  </button>
                  <button
                    className="action-button edit"
                    onClick={handleCreateProduct}
                  >
                    Ürün Oluştur
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Warehouse Creation Modal */}
        {showWarehouseModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowWarehouseModal(false)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Yeni Depo Oluştur</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowWarehouseModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Depo Adı *</label>
                  <input
                    type="text"
                    value={newWarehouse.ad}
                    onChange={(e) =>
                      setNewWarehouse({ ...newWarehouse, ad: e.target.value })
                    }
                    placeholder="Depo adını girin"
                  />
                </div>
                <div className="form-group">
                  <label>İl *</label>
                  <input
                    type="text"
                    value={newWarehouse.il}
                    onChange={(e) =>
                      setNewWarehouse({ ...newWarehouse, il: e.target.value })
                    }
                    placeholder="İl adını girin"
                  />
                </div>
                <div className="form-group">
                  <label>İlçe *</label>
                  <input
                    type="text"
                    value={newWarehouse.ilce}
                    onChange={(e) =>
                      setNewWarehouse({ ...newWarehouse, ilce: e.target.value })
                    }
                    placeholder="İlçe adını girin"
                  />
                </div>
                <div className="form-group">
                  <label>Detay Adres</label>
                  <textarea
                    value={newWarehouse.detay_adres}
                    onChange={(e) =>
                      setNewWarehouse({
                        ...newWarehouse,
                        detay_adres: e.target.value,
                      })
                    }
                    placeholder="Detay adres bilgilerini girin"
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>Telefon</label>
                  <input
                    type="tel"
                    value={newWarehouse.telefon}
                    onChange={(e) =>
                      setNewWarehouse({
                        ...newWarehouse,
                        telefon: e.target.value,
                      })
                    }
                    placeholder="Telefon numarasını girin"
                  />
                </div>
                <div className="form-group">
                  <label>E-posta</label>
                  <input
                    type="email"
                    value={newWarehouse.email}
                    onChange={(e) =>
                      setNewWarehouse({
                        ...newWarehouse,
                        email: e.target.value,
                      })
                    }
                    placeholder="E-posta adresini girin"
                  />
                </div>
                <div className="form-group">
                  <label>Kapasite *</label>
                  <input
                    type="number"
                    min="1"
                    value={newWarehouse.kapasite}
                    onChange={(e) =>
                      setNewWarehouse({
                        ...newWarehouse,
                        kapasite: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="Depo kapasitesini girin"
                  />
                </div>
                <div className="form-group">
                  <label>Depo Tipi</label>
                  <select
                    value={newWarehouse.depo_tipi}
                    onChange={(e) =>
                      setNewWarehouse({
                        ...newWarehouse,
                        depo_tipi: e.target.value,
                      })
                    }
                  >
                    <option value="genel">Genel Depo</option>
                    <option value="soguk_zincir">Soğuk Zincir</option>
                    <option value="tehlikeli_madde">Tehlikeli Madde</option>
                    <option value="ilaç">İlaç Deposu</option>
                    <option value="tıbbi_cihaz">Tıbbi Cihaz</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newWarehouse.soguk_zincir_destegi}
                      onChange={(e) =>
                        setNewWarehouse({
                          ...newWarehouse,
                          soguk_zincir_destegi: e.target.checked,
                        })
                      }
                    />
                    Soğuk Zincir Desteği
                  </label>
                </div>
                {newWarehouse.soguk_zincir_destegi && (
                  <>
                    <div className="form-group">
                      <label>Soğuk Zincir Sıcaklık (°C)</label>
                      <input
                        type="number"
                        value={newWarehouse.soguk_zincir_sicaklik}
                        onChange={(e) =>
                          setNewWarehouse({
                            ...newWarehouse,
                            soguk_zincir_sicaklik:
                              parseFloat(e.target.value) || 0,
                          })
                        }
                        placeholder="Sıcaklık değerini girin"
                      />
                    </div>
                    <div className="form-group">
                      <label>Minimum Sıcaklık (°C)</label>
                      <input
                        type="number"
                        value={newWarehouse.soguk_zincir_min}
                        onChange={(e) =>
                          setNewWarehouse({
                            ...newWarehouse,
                            soguk_zincir_min: parseFloat(e.target.value) || 0,
                          })
                        }
                        placeholder="Minimum sıcaklık"
                      />
                    </div>
                    <div className="form-group">
                      <label>Maksimum Sıcaklık (°C)</label>
                      <input
                        type="number"
                        value={newWarehouse.soguk_zincir_max}
                        onChange={(e) =>
                          setNewWarehouse({
                            ...newWarehouse,
                            soguk_zincir_max: parseFloat(e.target.value) || 0,
                          })
                        }
                        placeholder="Maksimum sıcaklık"
                      />
                    </div>
                  </>
                )}
                <div className="modal-actions">
                  <button
                    className="action-button delete"
                    onClick={() => setShowWarehouseModal(false)}
                  >
                    İptal
                  </button>
                  <button
                    className="action-button edit"
                    onClick={handleCreateWarehouse}
                  >
                    Depo Oluştur
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<"light" | "dark">("light");

  // Tema değiştirme fonksiyonu
  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
  };

  // Sayfa yüklendiğinde tema'yı localStorage'dan al
  useEffect(() => {
    const savedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setTheme(savedTheme);
    document.documentElement.setAttribute("data-theme", savedTheme);
  }, []);

  return (
    <AppProvider>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <AppContent />
      </ThemeContext.Provider>
    </AppProvider>
  );
};

export default App;
